// @owners { team: patients }
import { ToastContext } from '@alto/design-system';
import { useCallback, useContext } from 'react';
import { updateAutoRefill } from '~shared/actions/prescriptions';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { getIsCartShowing } from '~shared/selectors/ui/cart';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { useInvalidateMedDetails } from '../../med-details/queries/useMedDetails';
import { type OrderTypeContext } from '../constants';
import {
  type UpdateCartLike,
  persistOrderTypeForContext as runPersistOrderTypeForContext,
} from '../helpers/persistOrderType';

export const usePersistOrderType = ({
  prescriptionID,
  handleAddToCart,
  handleUpdateCartItem,
}: {
  readonly prescriptionID: number;
  readonly handleAddToCart: UpdateCartLike;
  readonly handleUpdateCartItem: UpdateCartLike;
}) => {
  const dispatch = useDispatchShared();
  const { addToast } = useContext(ToastContext);
  const { isPrescriptionInCart } = usePrescriptionsInCart();
  const isCartShowing = useSelectorShared(getIsCartShowing);
  const { invalidatePrescriptionsQuery, invalidateMedicationQuery } = useInvalidateMedDetails();

  const updateServerAutoRefillStatus = useCallback(
    (newAutoRefillStatus: boolean) => dispatch(updateAutoRefill(prescriptionID, newAutoRefillStatus)),
    [dispatch, prescriptionID],
  );

  const persistOrderTypeForContext = useCallback(
    async ({
      orderTypeContext,
      autoRefillEnabled,
    }: {
      readonly orderTypeContext: OrderTypeContext;
      readonly autoRefillEnabled: boolean;
    }) => {
      return runPersistOrderTypeForContext({
        orderTypeContext,
        handleUpdateCartItem,
        handleAddToCart,
        isPrescriptionInCart,
        prescriptionID,
        autoRefillEnabled,
        addToast,
        dispatch,
        isCartShowing,
        updateServerAutoRefillStatus,
        invalidatePrescriptionsQuery,
        invalidateMedicationQuery,
      });
    },
    [
      addToast,
      dispatch,
      handleAddToCart,
      handleUpdateCartItem,
      invalidateMedicationQuery,
      invalidatePrescriptionsQuery,
      isCartShowing,
      isPrescriptionInCart,
      prescriptionID,
      updateServerAutoRefillStatus,
    ],
  );

  return { persistOrderTypeForContext };
};
